/**
 * Tenant product capabilities from GET /api/auth/session → tenant object.
 * Prospect (no row or is_customer=false): full platform.
 * Customer: modules gated by has_core / has_procure.
 */

/** @typedef {{ tenant_id?: string, is_customer?: boolean, has_core?: boolean, has_procure?: boolean }} SessionTenant */

/**
 * @param {SessionTenant | null | undefined} tenant
 * @returns {{ showCore: boolean, showProcure: boolean, defaultRoute: string }}
 */
export function deriveCapabilities(tenant) {
  if (!tenant) {
    return { showCore: true, showProcure: true, defaultRoute: '/' };
  }
  const { is_customer, has_core, has_procure } = tenant;
  if (!is_customer) {
    return { showCore: true, showProcure: true, defaultRoute: '/' };
  }
  return {
    showCore: !!has_core,
    showProcure: !!has_procure,
    defaultRoute: has_procure && !has_core ? '/Procurement' : '/',
  };
}

/** Post-login / route-guard destination (maps `/` → `/Home`). */
export function resolvePostAuthRoute(capabilities) {
  const route = capabilities?.defaultRoute;
  if (!route || route === '/') return '/Home';
  return route;
}

const PUBLIC_PATH_PREFIXES = [
  '/',
  '/Landing',
  '/Auth',
  '/AuthCallback',
  '/ForgotPassword',
  '/ResetPassword',
];

const CORE_ROUTE_PREFIXES = [
  '/Home',
  '/Market',
  '/Accounts',
  '/People',
  '/Signals',
  '/Playbooks',
  '/Outbox',
  '/Analytics',
  '/Segments',
  '/Admin',
  '/SegmentBuilder',
  '/DataUploader',
  '/AccountFinal',
  '/PersonProfile',
  '/SegmentResults',
  '/DataManagement',
  '/PlaybookPreview',
  '/DIYPlaybook',
  '/EventPlaybookPreview',
  '/SettingsPersonalization',
  '/SettingsCommsSchedule',
  '/SettingsGmail',
  '/SettingsEvents',
  '/SettingsGmailCallback',
  '/SettingsProfile',
  '/SettingsOutlook',
  '/SettingsOutlookCallback',
  '/SettingsEmailTracking',
  '/TempDataUpdate',
  '/TempAccountFitUpload',
  '/OrphanedPeopleCleanup',
  '/ClientNameUpload',
  '/PeopleClientHistory',
  '/FixDemarqueMomentum',
];

const PROCURE_ROUTE_PREFIXES = ['/Procurement', '/AlertSettings'];

const PROCURE_SETTINGS_PREFIXES = ['/Settings', '/SettingsLanguage', '/SettingsProductProfile'];

function pathMatchesPrefix(pathname, prefixes) {
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function isPublicPath(pathname) {
  if (pathname === '/') return true;
  return PUBLIC_PATH_PREFIXES.some(
    (p) => p !== '/' && (pathname === p || pathname.startsWith(`${p}/`))
  );
}

/**
 * Whether the current path is allowed for this tenant's capabilities.
 * @param {string} pathname
 * @param {{ showCore: boolean, showProcure: boolean }} capabilities
 */
export function isPathAllowedForCapabilities(pathname, capabilities) {
  const path = (pathname || '').split('?')[0];
  if (isPublicPath(path)) return true;

  const { showCore, showProcure } = capabilities || { showCore: true, showProcure: true };

  if (showCore && showProcure) return true;

  if (!showCore) {
    return pathMatchesPrefix(path, [...PROCURE_ROUTE_PREFIXES, ...PROCURE_SETTINGS_PREFIXES]);
  }

  if (!showProcure) {
    return !pathMatchesPrefix(path, PROCURE_ROUTE_PREFIXES);
  }

  return true;
}

export const PROCURE_ONLY_SETTINGS_TABS = ['language', 'product-profile'];

export const CORE_SETTINGS_TABS = [
  'profile',
  'language',
  'gmail',
  'schedule',
  'personalization',
  'events',
  'product-profile',
];
